import { ItemCard } from "./ItemCard";
import { Button, Input } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUploadAlt,
  faFile,
  faImages,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  BuildDerivedImagesForSourceMutation,
  BuildDerivedImagesForSourceMutationVariables,
  SongUb,
} from "../../gql/graphql";
import { Link } from "react-router-dom";
import { sourceUrlsFor } from "../../helper/UrlResolver";
import { useDeleteSource } from "../useDeleteSource";
import { useRef, useState } from "react";
import Auth from "../../auth/Auth";
import { uploadFiles } from "./uploadFiles";
import { useMutation, UseMutationExecute } from "urql";
import { graphql } from "../../gql";
import { CyItem } from "./CyItem";
import { useTranslation, Trans } from "react-i18next";

const buildDerivedImagesForSourceMutation = graphql(`
  mutation buildDerivedImagesForSource(
    $bucket: String!
    $id: ID!
    $hash: String!
  ) {
    buildDerivedImagesForSource(bucket: $bucket, id: $id, hash: $hash) {
      ...SongFields
    }
  }
`);

interface Props {
  auth: Auth;
  song: SongUb;
  isDisabled?: boolean;
  onUploadCompleted?: () => void;
}

async function buildDerivedImagesForSource(
  executeBuildDerivedImagesMutation: UseMutationExecute<
    BuildDerivedImagesForSourceMutation,
    BuildDerivedImagesForSourceMutationVariables
  >,
  song: SongUb,
  hash: string,
) {
  const mutationResult = await executeBuildDerivedImagesMutation({
    bucket: song.bucket,
    id: song.id,
    hash,
  });
  console.debug(
    "buildDerivedImagesForSource result for hash",
    hash,
    mutationResult,
  );
  return Promise.resolve();
}

async function buildDerivedImages(
  result: {
    successCount: number;
    failedFiles: string[];
    uploadedHashes: string[];
  },
  executeBuildDerivedImagesMutation: UseMutationExecute<
    BuildDerivedImagesForSourceMutation,
    BuildDerivedImagesForSourceMutationVariables
  >,
  song: SongUb,
) {
  // Build derived images for each uploaded hash sequentially
  await result.uploadedHashes.reduce(async (accPromise, hash) => {
    await accPromise;
    return await buildDerivedImagesForSource(
      executeBuildDerivedImagesMutation,
      song,
      hash,
    );
  }, Promise.resolve());
}

export function SourceFilesCard({
  auth,
  song,
  isDisabled = false,
  onUploadCompleted,
}: Props) {
  const { t } = useTranslation();
  const { deleteSource, fetching } = useDeleteSource();
  const [deletingHash, setDeletingHash] = useState<string | undefined>(
    undefined,
  );
  const [regeneratingHash, setRegeneratingHash] = useState<string | undefined>(
    undefined,
  );
  const [, executeBuildDerivedImagesMutation] = useMutation(
    buildDerivedImagesForSourceMutation,
  );

  const [uploadFetching, setUploadFetching] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeleteSource = async (hash: string) => {
    if (isDisabled) {
      return;
    }

    if (!confirm(t("items.sourceFiles.confirmDelete"))) {
      return;
    }

    setDeletingHash(hash);
    const success = await deleteSource(song.bucket, song.id, hash);
    setDeletingHash(undefined);

    if (!success) {
      alert(t("items.sourceFiles.deleteError"));
    }
  };

  const handleBuildDerivedImages = async (hash: string) => {
    if (isDisabled) {
      return;
    }

    setRegeneratingHash(hash);
    await buildDerivedImagesForSource(
      executeBuildDerivedImagesMutation,
      song,
      hash,
    );
    setRegeneratingHash(undefined);
  };

  const processFiles = async (files: File[]) => {
    if (isDisabled) {
      return;
    }

    // Check max file limit
    if (files.length > 10) {
      alert(t("items.sourceFiles.maxFilesError"));
      return;
    }

    const token = auth.getAccessToken();
    if (!token) {
      console.error("Access token is missing");
      return;
    }

    // Validate file types
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        alert(
          t("items.sourceFiles.invalidFormatError", { fileName: file.name }),
        );
        return;
      }
    }

    setUploadFetching(true);
    const result: {
      successCount: number;
      failedFiles: string[];
      uploadedHashes: string[];
    } = await uploadFiles(files, song, token);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Show result
    if (result.failedFiles.length > 0) {
      alert(
        t("items.sourceFiles.uploadResultError", {
          successCount: result.successCount,
          totalFiles: files.length,
          failedFiles: result.failedFiles.join(", "),
        }),
      );
    }

    // Notify parent to refetch data if at least one file was uploaded
    if (result.successCount > 0) {
      onUploadCompleted?.();
    }

    await buildDerivedImages(result, executeBuildDerivedImagesMutation, song);
    setUploadFetching(false);
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const fileArray = Array.from(files);
    await processFiles(fileArray);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) {
      return;
    }

    const fileArray = Array.from(files);
    await processFiles(fileArray);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <ItemCard
      dataCy={CyItem.SongSourceCard}
      title={t("items.sourceFiles.title")}
      helpCollapsedArea={
        <>
          <ul>
            {t("items.sourceFiles.help", { returnObjects: true }).map(
              (step, i) => (
                <li key={i}>
                  <Trans>{step}</Trans>
                </li>
              ),
            )}
          </ul>
        </>
      }
    >
      {sourceUrlsFor(song).map(({ hash, url }, index) => {
        return (
          <div
            key={hash}
            className="d-flex justify-content-between align-items-center mb-2"
          >
            <Button
              tag={Link}
              to={url}
              color="primary"
              target="_blank"
              data-cy={CyItem.SourceFileLink}
              data-cy-key={`${index}-${hash}`}
            >
              <FontAwesomeIcon icon={faFile} className="me-1" />
              {song.title} {index > 0 ? `(${index + 1})` : ""}
            </Button>
            <div className="d-flex align-items-center">
              <Button
                color="primary"
                className="me-2"
                onClick={() => handleBuildDerivedImages(hash)}
                disabled={
                  isDisabled ||
                  fetching ||
                  deletingHash === hash ||
                  regeneratingHash === hash
                }
                data-cy={CyItem.BuildDerivedImagesForSourceButton}
                title={t("items.sourceFiles.rebuildImages")}
              >
                <FontAwesomeIcon icon={faImages} />
              </Button>
              <Button
                color="primary"
                onClick={() => handleDeleteSource(hash)}
                disabled={
                  isDisabled ||
                  fetching ||
                  deletingHash === hash ||
                  regeneratingHash === hash
                }
                data-cy={CyItem.DeleteSourceButton}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
          </div>
        );
      })}
      <div
        className="text-center p-4"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: isDragOver ? "2px dashed #007bff" : "2px dashed transparent",
          backgroundColor: isDragOver ? "#f0f8ff" : "transparent",
          borderRadius: "8px",
          transition: "all 0.2s ease",
        }}
      >
        <FontAwesomeIcon
          icon={faCloudUploadAlt}
          className="fas fa-3x text-muted mb-3"
        />
        <p className="text-muted">
          {isDragOver
            ? t("items.sourceFiles.dropHere")
            : t("items.sourceFiles.dragOrClick")}
        </p>
        <Input
          type="file"
          multiple={true}
          innerRef={fileInputRef}
          onChange={handleFileSelect}
          accept=".pdf,image/*"
          style={{ display: "none" }}
        />
        <Button
          color="primary"
          disabled={isDisabled || uploadFetching}
          onClick={handleButtonClick}
          data-cy={CyItem.UploadSourceFileButton}
        >
          {uploadFetching ? (
            t("items.sourceFiles.uploading")
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              {t("items.sourceFiles.addFile")}
            </>
          )}
        </Button>
      </div>
    </ItemCard>
  );
}
